import tradeDataStore from "@/store/tradeDataStore";
import tradeFromScratchStore from "@/store/tradeFromScratchStore";
import { removeDraftLegsForWidget } from "@/store/draftLegsStore";
import workspacesTabsStore from "@/store/workspacesTabsStore";
import {
  updateWorkspaceTabActionAdd,
  updateWorkspaceTabActionDelete,
  updateWorkspaceTabActionUpdate,
} from "@/store/workspacesTabsStoreACTIONS";
import { ColumnState, FilterModel } from "ag-grid-community";
import * as ReactDOMClient from "react-dom/client";
import { Layout } from "react-grid-layout";
import NewWindow from "react-new-window";
import { toast } from "react-toastify";
import { IbaseWidgetDetails, IWidgetDetails } from "./widgetsLayoutDir";

export function returnCurrentTabID() {
  const currentTabId = workspacesTabsStore
    .getState()
    .tabNamesAndActive.find((tab) => tab.active)?.tabId;
  return currentTabId;
}

// WARNING - This function needs to return an object with i of 'FirstLoad'. Requierd to load AG Charts with the Cube UI. See requirement 'Intergrate AG Charts into the web app' for info.
export function returnCurrentTabStateJSON() {
  const activeTab = workspacesTabsStore
    .getState()
    .tabNamesAndActive.find((tab) => tab.active);
  const currentTab = workspacesTabsStore
    .getState()
    .workspacesTabs.find((tab) => tab.tabId == activeTab?.tabId)?.stateJson ?? {
    // DO NOT DELETE This object with i of 'FirstLoad'. Requierd to load AG Charts with the Cube UI. See requirement 'Intergrate AG Charts into the web app' for info.
    currentBreakpoint: "lg",
    compactType: "vertical",
    mounted: false,
    layouts: {
      lg: [
        {
          w: 4,
          h: 15,
          x: 0,
          y: 0,
          i: "FirstLoad", // DO NOT DELETE
          minW: 2,
          moved: false,
          static: false,
        },
      ],
      sm: [
        {
          displayName: "FirstLoad",
          component: "FirstLoad",
          w: 6,
          h: 6,
          minW: 2,
          activeAnalyticsConfigIdNumber: -1,
          i: "FirstLoad", // DO NOT DELETE
          x: 0,
          y: 0,
        },
      ],
    },
    cols: {
      lg: 12,
      sm: 4,
    },
    openWidgets: [
      {
        displayName: "FirstLoad",
        component: "FirstLoad",
        w: 6,
        h: 6,
        minW: 2,
        activeAnalyticsConfigIdNumber: -1,
        i: "FirstLoad", // DO NOT DELETE
        x: 0,
        y: 0,
      },
    ],
  };
  return typeof currentTab === "string" ? JSON.parse(currentTab) : currentTab; // previously called currentTabStateJSON
}

export function returnOpenWidgetsArray() {
  const currentTabStateJSON = returnCurrentTabStateJSON();
  return currentTabStateJSON.openWidgets; // previously called openWidgetsArr
}

export function returnOpenWidgetsObject(cryptoID?: string | undefined) {
  const currentTabStateJSON = returnCurrentTabStateJSON();
  return currentTabStateJSON.openWidgets.find(
    (widget: any) => widget.i == cryptoID,
  ); // previously called openWidgetObject
}

// LQC-1740: Grid layout persistence is only armed for grids backed by a real widget
// in the workspace. The pop-out's synthetic cryptoID has no openWidgets entry, so it
// never persists.
export function isWidgetInCurrentWorkspace(cryptoID?: string | undefined) {
  if (!cryptoID) return false;
  return returnOpenWidgetsObject(cryptoID) != undefined;
}

interface IOpenWidgetsConfig {
  cryptoID?: string | undefined;
  columnStateForWidget: ColumnState[];
  filterModel: FilterModel;
  getAccessTokenSilently: Function;
}

// Take info from the column API and apply it to the STATEJSON on the open Widgets.
export async function updateOpenWidgetsWithTableConfigs({
  cryptoID,
  columnStateForWidget,
  filterModel,
  getAccessTokenSilently,
}: IOpenWidgetsConfig) {
  const currentTabStateJSON = returnCurrentTabStateJSON();
  const openWidgetsArr = returnOpenWidgetsArray();
  const openWidgetObject = returnOpenWidgetsObject(cryptoID);

  // openWidgetsArr is only getting called when loadedFromPopout is false. No hence openWidgetsArr!
  const arrWithUpdatedOpenWidgetData = openWidgetsArr!.map((widget: any) => {
    if (widget.i == openWidgetObject.i) {
      return {
        ...widget,
        columnStateForWidget: columnStateForWidget,
        filterModel: filterModel,
      };
    }
    return widget;
  });
  // Add the data to the widget.
  updateWorkspaceTabActionUpdate(
    arrWithUpdatedOpenWidgetData,
    currentTabStateJSON,
    getAccessTokenSilently,
    true,
  );
}
interface IOpenWidgetsPriceConfig {
  cryptoID?: string | undefined;
  activePricesConfigIdNumber: number;
  getAccessTokenSilently: Function;
}
export async function updateOpenWidgetsWithPriceConfigs({
  cryptoID,
  activePricesConfigIdNumber,
  getAccessTokenSilently,
}: IOpenWidgetsPriceConfig) {
  const currentTabStateJSON = returnCurrentTabStateJSON();
  const openWidgetsArr = returnOpenWidgetsArray();
  const openWidgetObject = returnOpenWidgetsObject(cryptoID!);

  // openWidgetsArr is only getting called when loadedFromPopout is false. No hence openWidgetsArr!
  const arrWithUpdatedOpenWidgetData = openWidgetsArr!.map((widget: any) => {
    if (widget.i == openWidgetObject.i) {
      return {
        ...widget,
        activePricesConfigIdNumber: activePricesConfigIdNumber,
      };
    }
    return widget;
  });
  // Add the data to the widget.
  updateWorkspaceTabActionUpdate(
    arrWithUpdatedOpenWidgetData,
    currentTabStateJSON,
    getAccessTokenSilently,
    true,
  );
}

// LQC-2207: the pop-out only ever loads active trades, so a historic ESID never
// resolves there. An ESID-less tile is still fine - the user picks one in the new window.
export function canPopOutTrade(esid: string | undefined | null) {
  if (!esid) return true;
  return tradeDataStore
    .getState()
    .tradeData.some((trade) => trade.esid == esid);
}

export function notifyTradeNotActive() {
  return toast.error("Only active trades can be popped out");
}

export function newPopOutWindow(widgetID: string, esid: string) {
  if (!canPopOutTrade(esid)) {
    notifyTradeNotActive();
    return;
  }

  const container = document.createElement("div");
  const root = ReactDOMClient.createRoot(container);

  // Put the ID of the most recently clicked on widget into a store. The pop out will need to read this.
  async function triggerGenerateWindow() {
    function generateWindow() {
      if (widgetID.includes("Tile")) {
        root.render(
          <NewWindow
            copyStyles={true}
            features={{ height: 350, width: 1200 }}
            url={`/Popout?component=TradeTile&esid=${esid}`}
          ></NewWindow>,
        );
      }
    }
    await generateWindow();
  }

  triggerGenerateWindow();
}
export function notifyAddWidget(displayName: string) {
  return toast(`Added ${displayName} widget`);
}

// LQC-1740: Cap widgets per workspace. Existing workspaces already over the cap
// still load, the limit only blocks adding more.
export const MAX_WIDGETS_PER_WORKSPACE = 20;

export function isWidgetLimitReached() {
  const openWidgets = returnOpenWidgetsArray();
  // FirstLoad is a hidden placeholder, not a user widget.
  const userWidgetCount = openWidgets.filter(
    (widget: IWidgetDetails) => widget.component !== "FirstLoad",
  ).length;
  return userWidgetCount >= MAX_WIDGETS_PER_WORKSPACE;
}

export function notifyWidgetLimitReached() {
  return toast.error(
    "Maximum amount of widgets already added. Remove a widget to add another.",
  );
}

export interface IbaseWidgetDetailsExtended extends IbaseWidgetDetails {
  esid?: string;
}
export function onAddWidget(
  item: IbaseWidgetDetails | IbaseWidgetDetailsExtended,
) {
  if (isWidgetLimitReached()) {
    notifyWidgetLimitReached();
    return;
  }
  notifyAddWidget(item.displayName);
  const currentTabStateJSON = returnCurrentTabStateJSON();
  // Use Actions function to return data to be set to store (DB happens on the layout change).
  updateWorkspaceTabActionAdd(item, currentTabStateJSON);
}

export function onDeleteItem(item: Layout) {
  const currentTabStateJSON = returnCurrentTabStateJSON();
  updateWorkspaceTabActionDelete(item, currentTabStateJSON);
  // LQC-2254 - per-widget state is dropped here rather than on unmount: unmount also
  // fires on a workspace tab switch, which would bin a ticket still being written.
  tradeFromScratchStore.getState().removeTickets(item.i);
  // LQC-2941 - a deleted Trade Creation widget drops its draft legs and their tickets.
  removeDraftLegsForWidget(item.i);
}
